import React, { useEffect, useRef, useState } from 'react';
import './metric-card.css';

/** One line of a tooltip breakdown, e.g. a facility and its inquiry count. */
export interface MetricBreakdownRow {
  id: string;
  label: string;
  value: string;
  /** Draws a rule above this row, setting an aggregate apart from the items above it. */
  divider?: boolean;
  /** Renders indented and quieter, as a detail line under the aggregate above it. */
  sub?: boolean;
}

interface MetricCardProps {
  label: string;
  value: string | number;
  sub?: string;
  info?: string;
  /** Itemized source of the metric. Empty or omitted renders the plain tooltip. */
  breakdown?: MetricBreakdownRow[];
  /** Heading above the breakdown rows, e.g. "By facility". */
  breakdownLabel?: string;
  loading?: boolean;
  placeholder?: boolean;
}

function renderBreakdownRow(row: MetricBreakdownRow): React.ReactElement {
  const className = [
    'mc-metric__breakdown-row',
    row.divider ? 'mc-metric__breakdown-row--divider' : '',
    row.sub ? 'mc-metric__breakdown-row--sub' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <span className={className} key={row.id}>
      <span className="mc-metric__breakdown-label">{row.label}</span>
      <span className="mc-metric__breakdown-value">{row.value}</span>
    </span>
  );
}

export function MetricCard({
  label,
  value,
  sub,
  info,
  breakdown,
  breakdownLabel,
  loading,
  placeholder,
}: MetricCardProps): React.ReactElement {
  const valueClass = [
    'mc-metric__value',
    loading ? 'loading-blur' : '',
    placeholder ? 'mc-metric__value--placeholder' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const hasBreakdown = (breakdown?.length ?? 0) > 0;
  const showInfo = Boolean(info) || hasBreakdown;

  // The icon's accessible name has to carry the breakdown too — a screen reader
  // gets no value from a hover-only table.
  const ariaLabel = [
    info,
    hasBreakdown
      ? `${breakdownLabel ?? 'Breakdown'}: ${breakdown!.map((r) => `${r.label} ${r.value}`).join(', ')}`
      : '',
  ]
    .filter(Boolean)
    .join('. ');

  const tooltipClass = ['mc-metric__tooltip', hasBreakdown ? 'mc-metric__tooltip--breakdown' : '']
    .filter(Boolean)
    .join(' ');

  // Hover alone can't hold a long breakdown open: reading or scrolling one means
  // keeping the pointer inside it the whole time. Clicking the icon pins it until
  // Escape, a click elsewhere, or another click on the icon.
  const [pinned, setPinned] = useState(false);
  const infoRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!pinned) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!infoRef.current?.contains(event.target as Node)) setPinned(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPinned(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [pinned]);

  const infoClass = ['mc-metric__info', pinned ? 'mc-metric__info--pinned' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className="mc-metric">
      <span className="mc-metric__label-row">
        <span className="mc-metric__label">{label}</span>
        {showInfo && (
          <button
            type="button"
            ref={infoRef}
            className={infoClass}
            aria-label={ariaLabel}
            aria-expanded={pinned}
            onClick={() => setPinned((wasPinned) => !wasPinned)}
          >
            <span className="mc-metric__info-icon" aria-hidden="true">
              i
            </span>
            <span className={tooltipClass} role="tooltip">
              {info && <span className="mc-metric__tooltip-note">{info}</span>}
              {hasBreakdown && (
                <>
                  {breakdownLabel && (
                    <span className="mc-metric__tooltip-heading">{breakdownLabel}</span>
                  )}
                  <span className="mc-metric__breakdown">{breakdown!.map(renderBreakdownRow)}</span>
                </>
              )}
            </span>
          </button>
        )}
      </span>
      <span className={valueClass}>{value}</span>
      {sub && <span className="mc-metric__sub">{sub}</span>}
    </div>
  );
}
